import { useState } from 'react';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { Detail } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import { formatNumber } from 'src/utils/numberFormatter';
import { UiMoney } from 'src/WebApiController';

type AdjustFxRateDialogProps = {
  isOpen: boolean;
  defaultRate: number;
  // Marketplace sale net proceeds in the original currency (e.g. USD amount from the exchange).
  // Used as the LHS of the preview: origAmt × newRate = newNetProceeds.
  mkpSaleTtlNetProcs: UiMoney | null;
  origCurcy: string | null;
  targetCurcy: string | null;
  onOkay: (rate: number) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

export const AdjustFxRateDialog = ({
  isOpen,
  defaultRate,
  mkpSaleTtlNetProcs,
  origCurcy,
  targetCurcy,
  onOkay,
  onCancel,
  isLoading,
}: AdjustFxRateDialogProps) => {
  const [rateInput, setRateInput] = useState(String(defaultRate));

  const parsedRate = parseFloat(rateInput);
  const isValid = !isNaN(parsedRate) && parsedRate > 0;

  // Preview: origAmt × newRate = newNetProceeds (in target currency)
  const previewAmount =
    isValid && mkpSaleTtlNetProcs != null
      ? mkpSaleTtlNetProcs.amt * parsedRate
      : null;

  const previewDisplay =
    previewAmount != null
      ? `${mkpSaleTtlNetProcs?.disp ?? ''} × ${rateInput} = ${formatNumber(previewAmount, mkpSaleTtlNetProcs?.dec ?? 2)} ${targetCurcy ?? ''}`
      : null;

  return (
    <GenericDialog
      isOpen={isOpen}
      size="sm"
      header={<Content id={ContentId.AdjustFxRate} />}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      footer={
        <>
          <CancelButton
            textContentId={ContentId.Cancel}
            onClick={onCancel}
            disabled={isLoading}
          />
          <OkButton
            textContentId={ContentId.Save}
            onClick={() => isValid && onOkay(parsedRate)}
            disabled={!isValid || isLoading}
          />
        </>
      }
    >
      <Stack direction="column" gap="l">
        <Detail
          label={<Content id={ContentId.NewFxRate} />}
          detail={
            <PosTextField
              type="number"
              step="0.000000001"
              min="0"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              errorState={!isValid && rateInput !== '' ? 'error' : undefined}
              postfixDisplay={
                origCurcy && targetCurcy
                  ? `${origCurcy} → ${targetCurcy}`
                  : undefined
              }
            />
          }
        />
        {previewDisplay && (
          <Detail
            label={<Content id={ContentId.CurrentFxRate} />}
            detail={<span>{previewDisplay}</span>}
          />
        )}
      </Stack>
    </GenericDialog>
  );
};
